/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * PRIMITIVE RASTER ESC/POS — texte → bitmap 1 bit → commande `GS v 0` → `<img>`
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * À quoi ça sert : faire sortir un ticket dont AUCUNE ligne n'est du texte, donc
 * qu'aucune règle de réécriture de l'app Android ne peut atteindre. C'est la technique
 * des grandes plateformes de livraison ; elle est ici confirmée réalisable avec le pont
 * v5.8 existant, SANS modification de l'app.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * LE FORMAT EXACT ATTENDU PAR LA BALISE `<img>` (relevé dans la source vendorisée)
 * ───────────────────────────────────────────────────────────────────────────────
 * `PrinterTextParserImg.hexadecimalStringToBytes()` fait
 * `Integer.parseInt(hex.substring(pos, pos+2), 16)` : la balise attend donc de
 * l'HEXADÉCIMAL, PAS du base64. Et le contenu décodé n'est PAS un PNG : ce sont
 * DIRECTEMENT les octets de la commande ESC/POS `GS v 0`, en-tête compris — le
 * constructeur relit `image[4..7]` comme `xL/xH/yL/yH`
 * (`PrinterTextParserImg:149-151`).
 *
 *     1D 76 30 00 xL xH yL yH   puis   (octetsParLigne × hauteur) octets de trame
 *      G  S  v  0  m=0  ↑        ↑
 *                       │        └── hauteur en PIXELS   (yL + yH*256)
 *                       └─────────── largeur en OCTETS   (xL + xH*256)
 *
 * Trame ligne par ligne, bit de POIDS FORT en premier, bit à 1 = point NOIR
 * (`EscPosPrinterCommands:145` → `b |= 1 << (7 - k)`).
 *
 * `useEscAsteriskCommand` n'est JAMAIS appelé par MainActivity → il reste à `false` →
 * `printImage()` écrit les octets `GS v 0` TELS QUELS sur la connexion
 * (`EscPosPrinterCommands:590`).
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * CONDITIONS DE PARSING — DURES, une seule erreur et l'hexa s'imprime EN CLAIR
 * ───────────────────────────────────────────────────────────────────────────────
 * `PrinterTextParserColumn:58` n'entre dans la voie image que si :
 *   • la ligne ne contient QU'UNE SEULE colonne (`getNbrColumns() == 1`) → donc UNE
 *     seule balise d'alignement, en tête, et RIEN d'autre sur la ligne ;
 *   • après `trim()`, la ligne commence par `<` ;
 *   • elle se termine EXACTEMENT par `</img>`.
 * Sinon `isImgOrBarcodeLine` reste faux et la ligne repart en rendu TEXTE : des mètres
 * de papier couverts d'hexadécimal.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * LARGEUR — deux valeurs et deux seulement
 * ───────────────────────────────────────────────────────────────────────────────
 * `MainActivity:4794` instancie `new AsyncEscPosPrinter(conn, 203, paperWidth, …)` avec
 * `paperWidth` = 72f (80 mm) ou 48f (58 mm), et `EscPosPrinterSize:20` calcule
 * `printerWidthPx = mmToPx + (mmToPx % 8)` :
 *   • 80 mm → mmToPx(72) = 575 → printerWidthPx = 582 ⇒ on émet 576 px (72 octets) ;
 *   • 58 mm → mmToPx(48) = 384 → printerWidthPx = 384 ⇒ on émet 384 px (48 octets).
 * Dans les deux cas `nbrByteDiff = floor((printerWidthPx - largeur) / 8) = 0`, donc
 * `PrinterTextParserImg:152-171` n'insère AUCUN octet blanc : l'image sort exactement où
 * on l'a composée. Toute autre largeur décalerait le rendu.
 *
 * ⚠️ Une image de 576 px envoyée à une imprimante réglée sur 58 mm déborde. La console ne
 * peut pas lire `paperWidth` (champ privé, aucune interface JS) : la largeur vient donc
 * du PROFIL, comme pour le module unifié.
 */

/** 80 mm : 576 px = 72 octets par ligne. */
export const RASTER_WIDTH_80MM = 576;
/** 58 mm : 384 px = 48 octets par ligne. */
export const RASTER_WIDTH_58MM = 384;

/**
 * Hauteur d'une BANDE d'image, en pixels.
 *
 * Le parseur n'impose aucun plafond (le `maxHeight = 256` d'`EscPosPrinterSize:93` est sur
 * le chemin `Bitmap → octets`, pas sur le chemin `hexa → octets`). On découpe quand même :
 * beaucoup de clones bas de gamme plafonnent un `GS v 0` unique à 255 ou 1023 lignes et
 * avalent silencieusement le reste. 128 lignes ≈ 16 mm de papier par bande.
 */
export const RASTER_BAND_HEIGHT = 128;

/**
 * Seuil de binarisation (0-255) : un pixel devient NOIR quand sa luminance est INFÉRIEURE
 * à ce seuil (voir la binarisation en bas de `rasterizeMonoLines` : `lum < threshold ? 1 : 0`).
 *
 * SENS VÉRIFIÉ : le texte est dessiné noir (#000, lum≈0) sur fond blanc (lum≈255). Les bords
 * anti-crénelés sont des GRIS intermédiaires. AUGMENTER le seuil fait basculer ces gris du
 * côté « noir » → traits plus ÉPAIS et plus PLEINS ; le baisser les laisse blancs → texte
 * fin et gris (le défaut constaté sur papier).
 *
 * RETOUR TERRAIN : réglé à 225 et VALIDÉ ainsi pour le CORPS (adresse, articles, totaux…). On
 * NE MONTE PLUS ce seuil GLOBAL : il s'applique à tous les pixels, donc le relever réencrerait
 * le corps que l'exploitant trouve déjà bon. La noirceur des BANDEAUX inversés ne dépend PAS du
 * seuil (leur fond est déjà à luminance 0, donc encre pleine quel que soit le seuil) — elle est
 * traitée séparément (voir ci-dessous et `rasterizePlanSlice`), pas ici.
 *
 * ⚠️ Blocs INVERSÉS : le pavé de fond est peint en `#000000` (aucun alpha, lissage coupé), donc
 * luminance 0 → encre à la binarisation, INDÉPENDAMMENT du seuil. Il est ensuite TRAMÉ à
 * ~`BANNER_FILL_DENSITY` (≈ 90 %) pour ne PAS envoyer un aplat 100 % (cause de surchauffe/strie).
 * Chaque bandeau est aussi émis comme SA PROPRE image (une seule bande GS v 0, jamais coupée par
 * une frontière de bande 128). Le texte du bandeau reste BLANC PLEIN par-dessus.
 */
const DEFAULT_THRESHOLD = 225;

/**
 * Densité de RÉFÉRENCE pour le calcul de la police : on dimensionne les glyphes pour faire
 * tenir ~82 % de `charsPerLine` (≈ 39 caractères en 80 mm au lieu de 48), ce qui rend les
 * glyphes nettement plus GROS — surtout sur les lignes courtes (adresse, date, articles
 * courts) qui paraissaient minuscules. Les lignes RÉELLEMENT pleines (jusqu'à charsPerLine)
 * ne sont PAS tronquées : la garde par-ligne du rendu réduit leur taille juste ce qu'il faut
 * pour rentrer dans la largeur physique.
 *
 * ⚠️ On ne touche JAMAIS à `widthPx` (576 px en 80 mm / 384 px en 58 mm) : c'est la largeur
 * physique du papier, et l'en-tête GS v 0 xL/xH en dépend directement (72 / 48 octets).
 * Agrandir le texte se fait donc UNIQUEMENT en réduisant la densité de caractères visée.
 */
const FONT_FILL_RATIO = 0.82;

/**
 * Plafond de la police de CORPS (px) calculée par `fitFontSize`. Ne borne PAS les lignes
 * agrandies : leur taille est `fontPx × scale` (nom 2,5× / infos clés 2×), non plafonnée ici —
 * elles passent à la ligne plutôt que d'être rognées. Relevé de 48 à 60 pour laisser respirer.
 */
const FONT_MAX_PX = 60;

/**
 * Densité d'encrage du FOND des bandeaux inversés (0-1). RÉGLABLE.
 *
 * Pourquoi < 1 : un aplat 100 % noir sur une grande surface fait SURCHAUFFER les têtes
 * thermiques bas de gamme, qui « coupent » alors en cours de ligne → stries grises et pavés
 * hachés (constaté sur le ticket #77). Le pont natif Android gère le timing d'impression (on n'a
 * pas la main sur un temps de repos DANS une image) ; l'équivalent LOGICIEL contre la surchauffe
 * est de ne PAS envoyer un aplat plein : on TRAME le fond à ~90 % de pixels noirs selon un motif
 * régulier dispersé. À l'œil ça reste noir, mais la charge thermique baisse → impression uniforme,
 * sans strie ni coupure. Le texte reste BLANC PLEIN par-dessus (non tramé). Descendre vers 0,87
 * si la tête surchauffe encore, remonter vers 0,94 si le fond paraît trop clair.
 */
const BANNER_FILL_DENSITY = 0.9;

export interface RasterLineInput {
  text: string;
  bold?: boolean;
  /** Multiplicateur de taille de police (1 = normal, 2 = titre). */
  scale?: number;
  /**
   * Centrer EN PIXELS plutôt qu'en espaces. Obligatoire pour les lignes agrandies : le
   * remplissage en espaces est calculé sur la grille de base et déborderait à `scale > 1`.
   */
  center?: boolean;
  /**
   * VIDÉO INVERSÉE : un bloc NOIR pleine largeur est peint sous la ligne, puis le texte est
   * dessiné en BLANC par-dessus. À l'impression thermique on obtient donc des lettres BLANCHES
   * (papier nu) sur un pavé NOIR (encre). Utilisé pour faire ressortir les infos clés
   * (n° de commande, type, table). Implique un centrage en pixels comme `center`.
   */
  reverse?: boolean;
  /**
   * INTERDIT le retour à la ligne pour une ligne AGRANDIE (`scale > 1`) : au lieu de wrapper, on
   * RÉDUIT l'échelle juste ce qu'il faut pour que tout tienne sur UNE ligne (auto-fit). Voulu
   * pour les bandeaux d'infos clés — « SUR PLACE - TABLE 3 » doit rester sur une seule ligne.
   * Sans ce drapeau, une ligne agrandie trop longue passe à la ligne (cas du NOM d'établissement).
   */
  noWrap?: boolean;
}

export interface RasterRenderOptions {
  /** 576 ou 384 — voir le bloc LARGEUR ci-dessus. */
  widthPx: number;
  /** Nombre de caractères par ligne visé (48 en 80 mm, 32 en 58 mm). */
  charsPerLine: number;
  bandHeightPx?: number;
  threshold?: number;
}

/* ═══════════════════════════════════════════════════════════════════════════════
 * GARDE-FOU — l'image n'existe QUE sur le chemin ESC/POS
 * ═══════════════════════════════════════════════════════════════════════════════
 * Vérifié dans MainActivity v5.8, `printToPrinter()` route sur `printer.getType()` :
 *   • `"sunmi"`    (MainActivity:5213) → `sunmiPrinterService.printText(text + "\n\n\n")`
 *     — texte BRUT, aucune balise interprétée : l'hexa s'imprimerait en clair ;
 *   • `"star_usb"` (MainActivity:5155) → `printWithStarUsb()`, SDK Star, balises
 *     simplement SUPPRIMÉES : même sinistre ;
 *   • `"star"`     (MainActivity:5070) → `printWithStar()`, le texte est DESSINÉ sur un
 *     Canvas Android (`StarIoExt.Emulation.StarGraphic`) : l'hexa serait dessiné.
 * Seuls `"usb"`, `"bluetooth"` et `"tcp"` passent par `getAsyncEscPosPrinter()` → DantSu.
 *
 * `getPrinterList()` (MainActivity:5730) expose ce type tel quel et construit
 * `id = type + "-" + address` : le type est donc LISIBLE côté console, dans les
 * identifiants d'imprimante enregistrés par le routage. On s'en sert pour REFUSER le
 * rendu image au lieu de le « déconseiller ».
 */

/** Types natifs qui ne comprennent PAS la balise `<img>`. */
const RASTER_INCOMPATIBLE_TYPES = ['sunmi', 'star_usb', 'star'];

export interface RasterTargetCheck {
  ok: boolean;
  /** Identifiants d'imprimantes incompatibles trouvés dans la configuration de routage. */
  blocked: string[];
}

/**
 * Type natif déduit d'un identifiant d'imprimante, construit côté Android sous la forme
 * `type + "-" + adresse` (MainActivity:5754). Les types composés utilisent un souligné
 * (`star_usb`) et jamais un tiret : la découpe au PREMIER `-` est donc exacte
 * (`star_usb-/dev/bus/usb/001/002` → `star_usb`).
 */
const printerTypeOf = (printerId: unknown): string =>
  String(printerId ?? '')
    .split('-')[0]
    .trim()
    .toLowerCase();

/* ═══════════════════════════════════════════════════════════════════════════════
 * ADAPTATION BORNE — le routage n'est PAS au même endroit que côté console
 * ═══════════════════════════════════════════════════════════════════════════════
 * Côté console, la configuration de routage est en localStorage (lecture SYNCHRONE).
 * Côté borne, elle est en IndexedDB (`printer-routing-db`, `printerRoutingService.ts`) :
 * lecture ASYNCHRONE, alors que la construction du ticket est SYNCHRONE. On conserve
 * donc un INSTANTANÉ en mémoire, rafraîchi par `preparePortedTicket()` (ticketModules/index.ts)
 * que l'appelant `await` AVANT de composer le ticket.
 *
 * ⚠️ TANT QUE L'INSTANTANÉ N'A PAS ÉTÉ PRIS, LE RENDU IMAGE EST REFUSÉ : sans cette règle,
 * un oubli d'appel enverrait de l'hexadécimal en clair sur une Star/Sunmi. Le refus ne
 * coûte rien (repli texte). On ne devine JAMAIS « au cas où ».
 */

let routingSnapshot: string[] | null = null;

/** Remplace l'instantané des cibles d'impression (identifiants `type-adresse`). */
export const setRasterRoutingSnapshot = (printerIds: Array<string | undefined | null>): void => {
  routingSnapshot = printerIds.filter((id): id is string => !!id);
};

/** Vide l'instantané → le rendu image redevient interdit (repli texte). */
export const clearRasterRoutingSnapshot = (): void => {
  routingSnapshot = null;
};

/**
 * Inspecte l'instantané de routage (BORNE) pour savoir si une imprimante incompatible
 * avec l'image y figure.
 *
 * ⚠️ Un instantané VIDE (routage désactivé, mode classique) = « aucune cible connue » :
 * le ticket part vers l'imprimante par défaut de l'app Android, dont le type n'est pas
 * lisible depuis le JS. On autorise l'image dans ce cas (comme la console), seul angle
 * mort restant, documenté ici plutôt que caché.
 */
export const checkRasterTargets = (): RasterTargetCheck => {
  const blocked: string[] = [];
  if (routingSnapshot === null) {
    // instantané pas encore pris → image refusée (repli texte sûr)
    return { ok: false, blocked: ['<routage-non-lu>'] };
  }
  for (const id of routingSnapshot) {
    if (RASTER_INCOMPATIBLE_TYPES.includes(printerTypeOf(id))) blocked.push(id);
  }
  return { ok: blocked.length === 0, blocked };
};

/** Le rendu image exige un `<canvas>` : hors navigateur (tests, SSR) il n'y en a pas. */
export const isCanvasAvailable = (): boolean => {
  try {
    if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
      return false;
    }
    const c = document.createElement('canvas');
    return !!(c && typeof c.getContext === 'function' && c.getContext('2d'));
  } catch {
    return false;
  }
};

/** Le rendu image est-il utilisable ICI et MAINTENANT ? (sinon : repli texte) */
export const isRasterUsable = (): boolean => isCanvasAvailable() && checkRasterTargets().ok;

/* ═══════════════════════════════════════════════════════════════════════════════
 * SÉRIALISATION `GS v 0` — fonction PURE, testable hors navigateur
 * ═══════════════════════════════════════════════════════════════════════════════ */

/** Table 0-255 → 2 caractères hexa minuscules (le parseur Java accepte les deux casses). */
const HEX_BYTE: string[] = (() => {
  const t = new Array<string>(256);
  for (let i = 0; i < 256; i += 1) t[i] = i.toString(16).padStart(2, '0');
  return t;
})();

/**
 * Trame monochrome (1 octet par pixel, valeur non nulle = NOIR) → bandes hexadécimales
 * `GS v 0` prêtes à être glissées dans une balise `<img>`.
 *
 * @param mono     tableau de `widthPx * heightPx` valeurs
 * @param widthPx  DOIT être un multiple de 8 (576 ou 384)
 */
export const monoToGsV0HexBands = (
  mono: Uint8Array,
  widthPx: number,
  heightPx: number,
  bandHeightPx: number = RASTER_BAND_HEIGHT
): string[] => {
  if (widthPx % 8 !== 0) {
    throw new Error(`escposRaster: largeur ${widthPx} px non multiple de 8`);
  }
  const bytesPerLine = widthPx >> 3;
  const band = Math.max(1, Math.floor(bandHeightPx));
  const bands: string[] = [];

  for (let y0 = 0; y0 < heightPx; y0 += band) {
    const h = Math.min(band, heightPx - y0);
    const parts: string[] = new Array(8 + bytesPerLine * h);

    // En-tête GS v 0 : xL/xH = largeur en octets, yL/yH = hauteur en pixels.
    parts[0] = HEX_BYTE[0x1d];
    parts[1] = HEX_BYTE[0x76];
    parts[2] = HEX_BYTE[0x30];
    parts[3] = HEX_BYTE[0x00];
    parts[4] = HEX_BYTE[bytesPerLine & 0xff];
    parts[5] = HEX_BYTE[(bytesPerLine >> 8) & 0xff];
    parts[6] = HEX_BYTE[h & 0xff];
    parts[7] = HEX_BYTE[(h >> 8) & 0xff];

    let p = 8;
    for (let y = 0; y < h; y += 1) {
      const rowOffset = (y0 + y) * widthPx;
      for (let bx = 0; bx < bytesPerLine; bx += 1) {
        const px = bx << 3;
        let b = 0;
        // Bit de poids fort = pixel le plus à GAUCHE.
        for (let k = 0; k < 8; k += 1) {
          if (mono[rowOffset + px + k]) b |= 1 << (7 - k);
        }
        parts[p] = HEX_BYTE[b];
        p += 1;
      }
    }
    bands.push(parts.join(''));
  }

  return bands;
};

/**
 * Ligne prête à imprimer pour une bande.
 * `[L]` et non `[C]` : avec `[L]`, `nbrWhiteByteToInsert` vaut 0 QUELLE QUE SOIT la
 * largeur réglée sur l'Android — aucune réécriture de la trame, aucun décalage possible.
 */
export const imgLine = (hexBand: string): string => `[L]<img>${hexBand}</img>`;

/* ═══════════════════════════════════════════════════════════════════════════════
 * RENDU CANVAS — texte à chasse fixe → trame monochrome
 * ═══════════════════════════════════════════════════════════════════════════════
 * On dessine du MONOSPACE et on réutilise le remplissage déjà calculé en JS : l'image est
 * donc une PHOTO du même ticket que les modules texte. C'est volontaire — la manip
 * compare des TECHNOLOGIES, pas des typographies.
 */

/**
 * Cherche la plus grande taille de police dont `fitChars` caractères tiennent dans `widthPx`,
 * où `fitChars` est VOLONTAIREMENT inférieur à `charsPerLine` (facteur `FONT_FILL_RATIO`) :
 * c'est le levier qui agrandit les glyphes sans toucher à la largeur physique de l'image.
 * Les lignes réellement pleines sont ensuite ramenées à la largeur par la garde par-ligne
 * du rendu (`while … measureText > widthPx`), donc jamais tronquées.
 */
const fitFontSize = (
  ctx: CanvasRenderingContext2D,
  widthPx: number,
  charsPerLine: number
): number => {
  const fitChars = Math.max(1, Math.round(charsPerLine * FONT_FILL_RATIO));
  const probe = 'M'.repeat(fitChars);
  let best = 8;
  for (let size = 8; size <= FONT_MAX_PX; size += 1) {
    ctx.font = `bold ${size}px "Courier New", monospace`;
    if (ctx.measureText(probe).width <= widthPx) best = size;
    else break;
  }
  return best;
};

/**
 * Découpe un texte AGRANDI en lignes qui tiennent chacune dans `maxWidthPx`, à la taille de
 * police COURANTE de `ctx` (déjà posée par l'appelant). Sert aux lignes en 2×/2,5× (nom
 * d'établissement, infos clés) : on les fait PASSER À LA LIGNE plutôt que de les rétrécir — un
 * nom long doit rester GROS, pas revenir à la taille du corps. Coupe dure si un mot seul déborde.
 */
const wrapToWidth = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidthPx: number
): string[] => {
  const fits = (s: string) => ctx.measureText(s).width <= maxWidthPx;
  const out: string[] = [];
  let cur = '';
  for (let word of text.split(' ').filter(Boolean)) {
    while (ctx.measureText(word).width > maxWidthPx && word.length > 1) {
      let cut = word.length;
      while (cut > 1 && ctx.measureText(word.slice(0, cut)).width > maxWidthPx) cut -= 1;
      if (cur) {
        out.push(cur);
        cur = '';
      }
      out.push(word.slice(0, cut));
      word = word.slice(cut);
    }
    const candidate = cur ? `${cur} ${word}` : word;
    if (!cur || fits(candidate)) cur = candidate;
    else {
      out.push(cur);
      cur = word;
    }
  }
  if (cur) out.push(cur);
  return out.length ? out : [text];
};

export interface RasterResult {
  /** Bandes hexadécimales `GS v 0`. */
  bands: string[];
  widthPx: number;
  heightPx: number;
  fontPx: number;
}

/**
 * Compose les lignes en image et renvoie les bandes hexadécimales.
 * Renvoie `null` si le rendu est impossible (pas de canvas) : l'appelant DOIT alors se
 * rabattre sur un rendu texte — jamais émettre d'hexadécimal « au cas où ».
 */
export const rasterizeMonoLines = (
  lines: RasterLineInput[],
  opts: RasterRenderOptions
): RasterResult | null => {
  if (!isCanvasAvailable()) return null;

  const widthPx = opts.widthPx;
  const threshold = opts.threshold ?? DEFAULT_THRESHOLD;

  const measure = document.createElement('canvas');
  const mctx = measure.getContext('2d');
  if (!mctx) return null;

  const fontPx = fitFontSize(mctx, widthPx, opts.charsPerLine);

  // Marge horizontale : bord du bloc inversé (le texte blanc ne touche pas le bord noir) et,
  // pour toutes les lignes, un peu de place pour le décalage de 1 px du FAUX GRAS.
  const hPadOf = (reverse?: boolean) => (reverse ? Math.round(fontPx * 0.4) : 2);
  const setFont = (c: CanvasRenderingContext2D, size: number) => {
    c.font = `bold ${size}px "Courier New", monospace`;
  };

  /* ── PASSE 1 : lignes d'ENTRÉE → lignes DESSINÉES (agrandissement + wrap/auto-fit) ──────
   * • NOM d'établissement (`scale > 1`, wrap autorisé) : taille pleine `fontPx × scale`, jamais
   *   rétrécie ; s'il déborde, il PASSE À LA LIGNE en gardant la grande taille.
   * • BANDEAUX d'infos clés (`scale > 1`, `noWrap`) : PAS de wrap → auto-fit sur UNE ligne, on
   *   part de 2× et on descend juste ce qu'il faut (« SUR PLACE - TABLE 3 » reste sur une ligne).
   * • CORPS (`scale 1`) : réduction jusqu'à tenir (comportement d'origine, inchangé). */
  // `heavy` = ligne CLÉ (nom d'établissement ou bandeau inversé, `scale > 1`) : rendue en FAUX
  // GRAS renforcé. Le CORPS (`scale 1`) est `heavy: false` → trait FIN (1 seule passe), taille
  // inchangée : l'exploitant a validé la TAILLE du corps, on n'en réduit QUE l'épaisseur.
  type DrawRow = { text: string; size: number; reverse: boolean; center: boolean; lineH: number; heavy: boolean };
  const rows: DrawRow[] = [];
  for (const l of lines) {
    const scale = l.scale && l.scale > 1 ? l.scale : 1;
    const reverse = !!l.reverse;
    const heavy = scale > 1;
    const maxTextWidth = Math.max(1, widthPx - hPadOf(reverse) * 2);

    if (!l.text) {
      const size = Math.max(1, Math.round(fontPx * scale));
      rows.push({
        text: '',
        size,
        reverse,
        center: !!l.center || reverse,
        lineH: Math.max(2, Math.round(fontPx * scale * 1.3)),
        heavy,
      });
      continue;
    }

    if (scale > 1 && !l.noWrap) {
      // NOM : agrandi, taille pleine ; wrap sur plusieurs lignes si trop large (jamais rétréci).
      const size = Math.max(1, Math.round(fontPx * scale));
      setFont(mctx, size);
      const parts =
        mctx.measureText(l.text).width <= maxTextWidth
          ? [l.text]
          : wrapToWidth(mctx, l.text, maxTextWidth);
      const lineH = Math.max(2, Math.round(size * 1.3));
      for (const p of parts) rows.push({ text: p, size, reverse, center: true, lineH, heavy });
    } else if (scale > 1) {
      // BANDEAU clé (`noWrap`) : UNE seule ligne. Auto-fit : de `fontPx × scale` on descend
      // jusqu'à ce que toute la ligne tienne dans la largeur → jamais de coupure en deux.
      let size = Math.max(1, Math.round(fontPx * scale));
      setFont(mctx, size);
      while (size > 1 && mctx.measureText(l.text).width > maxTextWidth) {
        size -= 1;
        setFont(mctx, size);
      }
      rows.push({ text: l.text, size, reverse, center: true, lineH: Math.max(2, Math.round(size * 1.3)), heavy });
    } else {
      // CORPS : réduction jusqu'à tenir (le canvas n'a pas de retour à la ligne). TAILLE INCHANGÉE.
      let size = Math.max(1, Math.round(fontPx));
      setFont(mctx, size);
      while (size > 1 && mctx.measureText(l.text).width > maxTextWidth) {
        size -= 1;
        setFont(mctx, size);
      }
      rows.push({ text: l.text, size, reverse, center: !!l.center, lineH: Math.max(2, Math.round(fontPx * 1.3)), heavy });
    }
  }

  const heightPx = rows.reduce((a, r) => a + r.lineH, 0);
  if (heightPx <= 0) return null;

  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Fond BLANC PLEIN, sans alpha ni lissage : garantit que les pavés noirs peints ensuite le
  // seront en `#000000` opaque (lum 0), donc en encre pleine à la binarisation.
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, widthPx, heightPx);
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  // Traits PLEINS et nets : on coupe le lissage d'image et on demande le rendu de texte le
  // plus « dur » possible. Moins de pixels gris intermédiaires en bord de trait = moins de
  // matière que la binarisation laisse en blanc = texte plus noir et moins « fin ».
  ctx.imageSmoothingEnabled = false;
  // `textRendering` n'est pas typé dans tous les lib.dom : accès défensif, sans effet si absent.
  try {
    (ctx as unknown as { textRendering?: string }).textRendering = 'geometricPrecision';
  } catch {
    /* propriété non supportée : le seuil relevé + le faux gras suffisent */
  }

  /* FAUX GRAS SÉLECTIF :
   *  • `heavy` (nom d'établissement, bandeaux inversés) → 5 passes (centre + 4 voisins cardinaux
   *    ±1 px) : trait épaissi SYMÉTRIQUEMENT, bien gras, pour rester en évidence.
   *  • CORPS (`heavy` faux : catégories, produits, options, totaux, pied) → 1 SEULE passe : trait
   *    FIN « comme avant ». Il reste NOIR grâce au seuil de binarisation élevé (225), pas au gras.
   * L'exploitant a validé la taille du corps : on n'en change QUE l'épaisseur, pas la taille. */
  const drawBold = (text: string, x: number, yTop: number, heavy: boolean) => {
    ctx.fillText(text, x, yTop);
    if (heavy) {
      ctx.fillText(text, x - 1, yTop);
      ctx.fillText(text, x + 1, yTop);
      ctx.fillText(text, x, yTop - 1);
      ctx.fillText(text, x, yTop + 1);
    }
  };

  // Rectangles des FONDS de bandeaux inversés (pleine largeur) : on les TRAMERA après coup pour
  // éviter l'aplat 100 % noir qui fait surchauffer la tête (voir `BANNER_FILL_DENSITY`).
  const bannerRects: Array<{ y0: number; y1: number }> = [];

  let y = 0;
  for (const r of rows) {
    if (r.text) {
      setFont(ctx, r.size);
      // Corps aligné à gauche à x=0 (1 passe, aucune passe `x-1` à absorber) ; lignes clés
      // centrées avec une marge (leur faux gras `x-1` reste dans le bloc/l'image).
      const textW = ctx.measureText(r.text).width;
      const drawX = r.center ? Math.max(hPadOf(r.reverse), Math.round((widthPx - textW) / 2)) : (r.heavy ? 1 : 0);
      const yTop = y + Math.max(0, Math.round((r.lineH - r.size * 1.15) / 2));

      if (r.reverse) {
        // PAVÉ qui ÉPOUSE le texte (marge fine) plutôt que toute la hauteur de ligne : moins de
        // surface noire = moins de charge thermique. Peint plein `#000000` ici ; le tramage (retrait
        // de ~10 % des pixels) est appliqué plus bas, à la binarisation, sur ces rectangles.
        const pad = Math.max(2, Math.round(r.size * 0.16));
        const top = Math.max(y, yTop - pad);
        const bot = Math.min(y + r.lineH, yTop + Math.round(r.size * 1.15) + pad);
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, top, widthPx, Math.max(1, bot - top));
        bannerRects.push({ y0: top, y1: Math.min(heightPx, bot) });
        ctx.fillStyle = '#ffffff'; // texte BLANC PLEIN par-dessus (non tramé)
      } else {
        ctx.fillStyle = '#000000';
      }
      drawBold(r.text, drawX, yTop, r.heavy);
    }
    y += r.lineH;
  }

  const img = ctx.getImageData(0, 0, widthPx, heightPx);
  const src = img.data;
  const mono = new Uint8Array(widthPx * heightPx);
  for (let i = 0, j = 0; i < mono.length; i += 1, j += 4) {
    const alpha = src[j + 3];
    // Un pixel transparent n'est pas encré ; sinon on binarise sur la luminosité.
    const lum = alpha < 128 ? 255 : (src[j] + src[j + 1] + src[j + 2]) / 3;
    mono[i] = lum < threshold ? 1 : 0;
  }

  /* TRAMAGE DENSE des FONDS de bandeaux inversés (anti-surchauffe — voir `BANNER_FILL_DENSITY`).
   * On retire ~(1 - densité) des pixels NOIRS du fond selon un motif régulier DISPERSÉ
   * `(3x + 7y) % N == 0` : 3 et 7 sont premiers avec 10, donc les trous blancs forment une trame
   * diagonale répartie — aucune ligne pleine horizontale/verticale, donc pas de strie induite. On
   * ne touche QUE les pixels de FOND encore noirs (`mono === 1`) : le texte blanc (déjà `0`) reste
   * intact et PLEIN. À l'œil le fond paraît noir ; la tête, elle, chauffe moins. */
  if (bannerRects.length) {
    const white = Math.max(0.01, 1 - BANNER_FILL_DENSITY);
    const N = Math.max(2, Math.round(1 / white));
    for (const rect of bannerRects) {
      for (let yy = Math.max(0, rect.y0); yy < rect.y1; yy += 1) {
        const rowBase = yy * widthPx;
        for (let xx = 0; xx < widthPx; xx += 1) {
          const idx = rowBase + xx;
          if (mono[idx] === 1 && (xx * 3 + yy * 7) % N === 0) mono[idx] = 0;
        }
      }
    }
  }

  return {
    bands: monoToGsV0HexBands(mono, widthPx, heightPx, opts.bandHeightPx ?? RASTER_BAND_HEIGHT),
    widthPx,
    heightPx,
    fontPx,
  };
};

/** Lignes `[L]<img>…</img>` prêtes à concaténer, ou `null` si le rendu est impossible. */
export const rasterizeToImgLines = (
  lines: RasterLineInput[],
  opts: RasterRenderOptions
): string[] | null => {
  const res = rasterizeMonoLines(lines, opts);
  return res ? res.bands.map(imgLine) : null;
};
